import React, { useEffect, useState, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from "../components/ui/Input";
import { Product } from '../types';
import { useProductsApi } from '../apis/products-apis';

interface ProductSelectProps {
  selectedProduct: string;
  onChange: (productName: string) => void;
  readOnly?: boolean;
}

export const ProductSelect: React.FC<ProductSelectProps> = ({
  selectedProduct,
  onChange,
  readOnly = false
}) => {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const productsApi = useProductsApi();

  // Load all products on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const products = await productsApi.getProducts();
        setAllProducts(products);
        setError(null);
      } catch (err) {
        setError('Failed to load products');
        console.error('Error fetching products:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter products based on search query
  const filteredProducts = searchQuery.length >= 1 ? allProducts.filter(product => {
    return product.productName.toLowerCase().includes(searchQuery.toLowerCase());
  }) : [];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsDropdownOpen(value.length >= 1);
  };

  const handleSelectProduct = (productName: string) => {
    if (readOnly) return;
    
    onChange(productName);
    setSearchQuery('');
    setIsDropdownOpen(false);
    inputRef.current?.focus();
  };

  const handleAddNewProduct = () => {
    if (readOnly || searchQuery.trim() === '') return;
    
    onChange(searchQuery.trim());
    setSearchQuery('');
    setIsDropdownOpen(false);
  };

  const clearSelection = () => {
    if (readOnly) return;
    onChange('');
  };

  const ProductViewDisplay = ({ productName }: { productName: string }) => {
    if (!productName) {
      return <div className="text-gray-500 italic">No product assigned</div>;
    }
    
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
        <span className="text-sm text-blue-700">{productName}</span>
      </div>
    );
  };

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="space-y-4">
      {readOnly ? (
        <ProductViewDisplay productName={selectedProduct} />
      ) : (
        <>
          {/* Selected Product Display */}
          {selectedProduct && (
            <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 flex items-center justify-between">
              <span className="text-sm text-blue-700">{selectedProduct}</span>
              <button
                onClick={clearSelection}
                className="text-blue-400 hover:text-blue-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Search Input and Dropdown */}
          {!selectedProduct && (
            <div className="relative" ref={dropdownRef}>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="Search products or enter a new one..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-10"
                  disabled={isLoading}
                />
              </div>

              {/* Dropdown Results */}
              {isDropdownOpen && searchQuery.length >= 1 && (
                <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
                  {isLoading ? (
                    <div className="p-4 text-center text-gray-500">Loading products...</div>
                  ) : (
                    <div className="py-1">
                      {filteredProducts.length > 0 ? (
                        filteredProducts.map(product => (
                          <div
                            key={product.productId}
                            onClick={() => handleSelectProduct(product.productName)}
                            className="px-4 py-2 cursor-pointer hover:bg-gray-50"
                          >
                            <div className="font-medium text-gray-900">{product.productName}</div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2">
                          <div className="text-gray-500">No products found matching your search</div>
                          <button
                            onClick={handleAddNewProduct}
                            className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                          >
                            + Add "{searchQuery}" as a new product
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductSelect;